import type { ErrorInfo, ReactNode } from 'react';
import React, { Suspense } from 'react';
import { Trans } from '@lingui/react/macro';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * ErrorBoundary behavior:
 *
 * Only catches errors that happen:
 *   • during rendering
 *   • in lifecycle methods
 *   • in constructors of any component below them in the tree
 *
 * Does NOT catch:
 *   • Asynchronous errors (e.g. a failed fetch() in a useEffect)
 *   • Event handler errors (e.g. inside an onClick callback)
 *   • Errors thrown in the Error Boundary itself
 *   • Server-side errors (e.g. SSR) or errors outside of React’s rendering phase
 */

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // You can also log the error to an error reporting service
        console.log(error, errorInfo);
    }

    render() {
        const { hasError } = this.state;
        const { children } = this.props;

        // TODO: turnOnErrorBoundary later
        const turnOnErrorBoundary = false;
        if (hasError && turnOnErrorBoundary) {
            // You can render any custom fallback UI
            return (
                <Suspense fallback="">
                    <div>
                        <p>
                            <Trans>Something went wrong</Trans>
                        </p>
                        <button onClick={() => window.location.reload()}>
                            <Trans>Reload</Trans>
                        </button>
                    </div>
                </Suspense>
            );
        }

        return children;
    }
}

export default ErrorBoundary;
